import { type OAuthFetchClient } from '../../custom-ui/bridge/methods/useProductFetchClient';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';
import { fg } from '@atlaskit/platform-feature-flags';
import { BridgeClientError } from '../../custom-ui/bridge';

export const TEAMWORK_GRAPH_GATEWAY_PATH = '/gateway/api/graphql/twg'; // e.g. https://api.atlassian.com/gateway/api/graphql/twg

export type RequestTeamworkGraphPayload = {
	query: string;
	variables?: Record<string, unknown>;
};

export type RequestTeamworkGraphResponse = {
	data: Record<string, unknown> | null | undefined;
	errors?: Record<string, unknown>[] | undefined;
};

type RequestTeamworkGraphContext = MakeBridgeContext<'contextIds', 'productFetchClient'>;

const teamworkGraphFetchClient = async (
	productFetchClient: OAuthFetchClient,
	contextId: string,
	{ query, variables }: RequestTeamworkGraphPayload,
): Promise<RequestTeamworkGraphResponse> => {
	const body = JSON.stringify({ query, variables });
	const headers = new Headers({
		'content-type': 'application/json',
		'X-Query-Context': `${contextId}`,
	});

	const response = await productFetchClient(
		TEAMWORK_GRAPH_GATEWAY_PATH,
		'teamwork-graph',
		{ method: 'POST', headers, body },
		false,
	);

	if (!response.ok) {
		const errorText = await response.text();
		throw new BridgeClientError(
			`Unexpected error encountered in requestTeamworkGraph. Status: ${response.status} ${response.statusText}. Response: ${errorText}`,
		);
	}

	const { data, errors } = await response.json();
	return { data, errors };
};

/**
 * Makes an authenticated call to the Teamwork Graph GraphQL API on behalf of the Forge app
 * running inside the iframe. Similar to fetchProduct, and uses the same productFetchClient.
 *
 * Returns the parsed GraphQL response body as an object (`{ data, errors }`).
 *
 * @param data.query - The raw graphql query
 * @param data.variables - The variables to pass to the graphql query
 * @returns The parsed response data
 */
export const requestTeamworkGraph: TBridgeMethod<
	RequestTeamworkGraphContext,
	RequestTeamworkGraphPayload,
	RequestTeamworkGraphResponse
> = async ({ services: { productFetchClient }, contextIds }, payload) => {
	if (!fg('forge-ui-enable-requestteamworkgraph-bridge-method')) {
		throw new BridgeClientError('TeamWork Graph API is not available for this instance.');
	}
	const contextId = contextIds[0];

	return teamworkGraphFetchClient(productFetchClient, contextId, payload);
};
